import { createApi } from '@reduxjs/toolkit/query/react';
import { prepareBaseQuery } from '../shared/configs/config';

export interface BatchApprovalRule {
  id: string;
  customer_id: string;
  name: string;
  property_ids: string[] | null;
  min_amount: number | null;
  max_amount: number | null;
  approver_user_ids: string[];
  approver_role_label: string;
  priority: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BatchApprovalRuleCreateInput {
  customer_id: string;
  name: string;
  property_ids?: string[] | null;
  min_amount?: number | null;
  max_amount?: number | null;
  approver_user_ids: string[];
  approver_role_label: string;
  priority?: number;
  active?: boolean;
}

export type BatchApprovalRuleUpdateInput = Partial<
  Omit<BatchApprovalRuleCreateInput, 'customer_id'>
> & { id: string };

export interface ListQueryArgs {
  customer_id?: string;
  active?: boolean;
}

export const batchApprovalRulesApi = createApi({
  reducerPath: 'batchApprovalRulesApi',
  tagTypes: ['BatchApprovalRules'],
  baseQuery: prepareBaseQuery(),
  endpoints: (builder) => ({
    listBatchApprovalRules: builder.query<BatchApprovalRule[], ListQueryArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args.customer_id) params.set('customer_id', args.customer_id);
        if (args.active !== undefined) params.set('active', String(args.active));
        const qs = params.toString();
        return {
          url: `/batch-approval-rule${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['BatchApprovalRules'],
    }),
    getBatchApprovalRule: builder.query<BatchApprovalRule, string>({
      query: (id) => ({ url: `/batch-approval-rule/${id}`, method: 'GET' }),
      providesTags: ['BatchApprovalRules'],
    }),
    createBatchApprovalRule: builder.mutation<BatchApprovalRule, BatchApprovalRuleCreateInput>({
      query: (body) => ({
        url: `/batch-approval-rule`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['BatchApprovalRules'],
    }),
    updateBatchApprovalRule: builder.mutation<BatchApprovalRule, BatchApprovalRuleUpdateInput>({
      query: ({ id, ...body }) => ({
        url: `/batch-approval-rule/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['BatchApprovalRules'],
    }),
    deactivateBatchApprovalRule: builder.mutation<BatchApprovalRule, string>({
      query: (id) => ({ url: `/batch-approval-rule/${id}`, method: 'DELETE' }),
      invalidatesTags: ['BatchApprovalRules'],
    }),
  }),
});

export const {
  useListBatchApprovalRulesQuery,
  useGetBatchApprovalRuleQuery,
  useCreateBatchApprovalRuleMutation,
  useUpdateBatchApprovalRuleMutation,
  useDeactivateBatchApprovalRuleMutation,
} = batchApprovalRulesApi;
